import * as React from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import Fieldset from 'src/components/project-billing/components/FieldSetComponent';
import { IArHistorySearchCriteria } from '../AccountsReceivableServiceContext';

interface IArHistorySearchProps {
    setIsCollapsed?: (isCollapsed: boolean) => void;
    runQuery: (criteria: IArHistorySearchCriteria) => void;
}

// Same values as ar_pastdue.fmt's INVOICE_TYPE poplist - "All" isn't a real Oracle list entry,
// but both forms' Query triggers already treat invoice_type = 3 as "both".
const INVOICE_TYPE_OPTIONS = [
    { value: '3', label: 'All' },
    { value: '1', label: 'Expense Invoices' },
    { value: '2', label: 'Lease Invoices' },
];

// Mirrors arhistory.fmt's INVOICE_STATUS poplist (oid 49: 1 = close='Y', 2 = close='N').
const INVOICE_STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: '1', label: 'Closed' },
    { value: '2', label: 'Open' },
];

// Mirrors the Control block's search fields (Client No., Prospect No., Beg/End Date, Invoice
// Type, Invoice Status) and its Query button (oid 49). The Collapse/Clear pair is carried over
// from CheckRegisterSearch.tsx's own query-by-example pattern rather than anything in the Oracle
// form. Report/Excel (oid 52/73) live in their own row above the grid on the page itself, not
// in this search panel - see ArHistoryPage.tsx.
const ArHistorySearch = (props: IArHistorySearchProps) => {
    const { register, errors, setValue, getValues } = useForm();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

    const toNumber = (numStr: string) => (!!numStr && !isNaN(Number(numStr)) ? Number(numStr) : undefined);

    const executeSearch = () => {
        const values = getValues();
        const criteria: IArHistorySearchCriteria = {
            clientNumber: toNumber(values.clientNumber),
            projectNumber: toNumber(values.projectNumber),
            begDate: values.begDate || undefined,
            endDate: values.endDate || undefined,
            invoiceType: values.invoiceType || '3',
            invoiceStatus: values.invoiceStatus || undefined,
        };
        props.runQuery(criteria);
    };

    const clearSearchFilters = () => {
        setValue('clientNumber', '');
        setValue('projectNumber', '');
        setValue('begDate', '');
        setValue('endDate', '');
        setValue('invoiceType', '3');
        setValue('invoiceStatus', '');

        props.runQuery({ invoiceType: '3' });
    };

    const handleSetIsCollapsed = (collapsed: boolean) => {
        setIsCollapsed(collapsed);
        if (props?.setIsCollapsed) {
            props.setIsCollapsed(collapsed);
        }
    };

    if (isCollapsed) {
        return (
            <div style={{ textAlign: 'center' }}>
                <Button
                    text="(+) Search Filters"
                    className="blue-hover-button"
                    style={{ height: '2rem', margin: '0', padding: '.2rem' }}
                    onClick={() => handleSetIsCollapsed(false)}
                />
            </div>
        );
    }

    return (
        <Page style={{ justifyContent: 'center' }}>
            <Page.Section width={12}>
                <Panel backgroundColor="#D3D3D3">
                    <Panel panelTitle="A/R History Search">
                        <form>
                            <div className="form-row" style={{ padding: '.5rem', margin: '0', flexWrap: 'nowrap', alignItems: 'flex-end' }}>
                                <div style={{ width: '12%' }}>
                                    <Fieldset label="Client No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="clientNumber" type="number" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '12%', paddingLeft: '4px' }}>
                                    <Fieldset label="Prospect No." style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="projectNumber" type="number" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '13%', paddingLeft: '4px' }}>
                                    <Fieldset label="Beg Date" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="begDate" type="date" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '13%', paddingLeft: '4px' }}>
                                    <Fieldset label="End Date" style={{ border: 0, padding: 0 }}>
                                        <RHFInputBox name="endDate" type="date" register={register} errors={errors} style={{ width: '100%' }} bodyStyle={{ width: '100%' }} />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '16%', paddingLeft: '4px' }}>
                                    <Fieldset label="Invoice Type" style={{ border: 0, padding: 0 }}>
                                        <RHFSelect
                                            name="invoiceType"
                                            options={INVOICE_TYPE_OPTIONS}
                                            defaultVal="3"
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ width: '13%', paddingLeft: '4px' }}>
                                    <Fieldset label="Invoice Status" style={{ border: 0, padding: 0 }}>
                                        <RHFSelect
                                            name="invoiceStatus"
                                            options={INVOICE_STATUS_OPTIONS}
                                            defaultVal=""
                                            register={register}
                                            errors={errors}
                                            style={{ width: '100%' }}
                                        />
                                    </Fieldset>
                                </div>
                                <div style={{ display: 'flex', marginLeft: 'auto', flexShrink: 0, paddingLeft: '1rem' }}>
                                    <Button
                                        text="Search"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', width: '7rem', height: '3rem', verticalAlign: 'middle', marginLeft: '1rem', borderRadius: '5px' }}
                                        onClick={() => executeSearch()}
                                    />
                                    <Button
                                        text="(-) Minimize"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '9rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                        onClick={() => handleSetIsCollapsed(true)}
                                    />
                                    <Button
                                        text="Clear"
                                        className="blue-hover-button"
                                        style={{ whiteSpace: 'nowrap', marginLeft: '1rem', width: '7rem', height: '3rem', verticalAlign: 'middle', borderRadius: '5px' }}
                                        onClick={() => clearSearchFilters()}
                                    />
                                </div>
                            </div>
                        </form>
                    </Panel>
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default ArHistorySearch;



// WEBPACK FOOTER //
// ./src/components/accounts-receivable/components/ArHistorySearch.tsx